import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { getPrompt } from "@/api/prompts";
import { Modal } from "@/components/ui/modal";

interface PromptPreviewModalProps {
  kind: string | null;
  open: boolean;
  onClose: () => void;
}

export function PromptPreviewModal({ kind, open, onClose }: PromptPreviewModalProps) {
  const { data: prompt, isLoading, error } = useQuery({
    queryKey: ["prompt", "preview", kind],
    queryFn: () => getPrompt(kind!),
    enabled: open && !!kind,
    staleTime: 60_000,
  });

  return (
    <Modal open={open && !!kind} onClose={onClose} labelledBy="prompt-preview-title">
      <div className="flex items-start justify-between gap-4 border-b border-border bg-surface px-5 py-4">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            System prompt
          </p>
          <h2 id="prompt-preview-title" className="truncate text-base font-semibold text-foreground">
            {prompt?.name ?? kind}
          </h2>
          {prompt?.description && (
            <p className="mt-1 text-xs text-muted-foreground">{prompt.description}</p>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="shrink-0 rounded-md p-1.5 text-muted-foreground transition hover:bg-surface-hover hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto bg-surface px-5 py-4">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted border-t-brand" />
          </div>
        )}

        {error && (
          <p className="text-sm text-destructive">Could not load this prompt template.</p>
        )}

        {prompt && (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Read-only preview. This system prompt is managed at the app level in Settings → Prompts.
              Placeholders like{" "}
              <code className="rounded bg-surface-hover px-1 py-0.5 font-mono text-[10px]">
                {"{{JOB_COMPANY}}"}
              </code>{" "}
              are filled in with your job and profile data when research runs.
            </p>

            <div className="rounded-md border border-border bg-[var(--pre-bg)]">
              <pre className="max-h-[50vh] overflow-auto whitespace-pre-wrap p-4 font-mono text-xs leading-relaxed text-foreground">
                {prompt.systemPrompt || "(No system prompt text)"}
              </pre>
            </div>

            {prompt.outputContract && (
              <div>
                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Output contract
                </p>
                <p className="rounded-md border border-border bg-[var(--pre-bg)] px-3 py-2 font-mono text-xs text-foreground">
                  {prompt.outputContract}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-border bg-surface px-5 py-3">
        {prompt && (
          <div className="flex flex-wrap gap-2 text-[10px] text-muted-foreground">
            <span className="rounded-full bg-surface-hover px-2 py-0.5">v{prompt.version}</span>
            <span className="rounded-full bg-surface-hover px-2 py-0.5">{prompt.kind}</span>
            {prompt.category && (
              <span className="rounded-full bg-surface-hover px-2 py-0.5">{prompt.category}</span>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={onClose}
          className="ml-auto rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-foreground transition hover:bg-surface-hover"
        >
          Close
        </button>
      </div>
    </Modal>
  );
}
